import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Clock, CalendarDays } from "lucide-react";
import Seo from "@/components/Seo";
import XLogo from "@/components/XLogo";
import { API, scrollToId } from "@/data/content";

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
    setArticle(null);
    setMissing(false);
    axios
      .get(`${API}/articles/${slug}`)
      .then(({ data }) => setArticle(data))
      .catch(() => setMissing(true));
  }, [slug]);

  const jsonLd = article
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.title,
        description: article.excerpt,
        image: article.image,
        datePublished: article.published_at,
        author: { "@type": "Organization", name: "TEAM X" },
        publisher: { "@type": "Organization", name: "TEAM X" },
      }
    : null;

  return (
    <div data-testid="article-page" className="min-h-screen bg-void">
      {article && (
        <Seo
          title={`${article.title} — TEAM X`}
          description={article.excerpt}
          type="article"
          image={article.image}
          jsonLd={jsonLd}
        />
      )}
      <header className="glass sticky top-0 z-50 border-b border-line">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2.5">
            <XLogo size={28} />
            <span className="font-display text-xl font-black uppercase tracking-[0.18em] text-ink">
              Team<span className="text-brand">&nbsp;X</span>
            </span>
          </Link>
          <Link
            data-testid="article-back-link"
            to="/insights"
            className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-mute transition-colors hover:text-brand"
          >
            <ArrowLeft size={13} /> All Insights
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-14 sm:px-6">
        {missing && (
          <div className="py-20 text-center">
            <h1 className="font-display text-3xl font-black uppercase text-ink">
              Article not found
            </h1>
            <Link
              to="/insights"
              className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-brand"
            >
              <ArrowLeft size={13} /> Back to insights
            </Link>
          </div>
        )}
        {!article && !missing && (
          <p className="py-20 text-center font-mono text-xs uppercase tracking-[0.2em] text-dim">
            Loading article…
          </p>
        )}
        {article && (
          <article>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-brand">
              {article.category}
            </p>
            <h1 className="mt-3 font-display text-3xl font-black uppercase leading-[0.95] tracking-tight text-ink sm:text-5xl">
              {article.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-[0.18em] text-dim">
              <span className="text-mute">{article.author}</span>
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays size={12} />
                {new Date(article.published_at).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock size={12} /> {article.read_minutes} min read
              </span>
            </div>

            <img
              src={article.image}
              alt={article.title}
              className="clip-frame mt-8 aspect-[2/1] w-full rounded-xl border border-line object-cover"
            />

            <div data-testid="article-content" className="mt-10 space-y-6">
              {(article.content || []).map((block, i) =>
                block.startsWith("## ") ? (
                  <h2
                    key={i}
                    className="pt-4 font-display text-2xl font-bold uppercase tracking-tight text-ink"
                  >
                    {block.slice(3)}
                  </h2>
                ) : (
                  <p key={i} className="text-sm leading-relaxed text-mute sm:text-base">
                    {block}
                  </p>
                )
              )}
            </div>

            <div className="mt-10 flex flex-wrap gap-2 border-t border-line pt-6">
              {(article.tags || []).map((t) => (
                <span
                  key={t}
                  className="rounded-full border border-line px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-dim"
                >
                  {t}
                </span>
              ))}
            </div>

            <div className="card-trace mt-10 rounded-xl border border-line bg-panel p-7">
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-brand">
                Continue Learning
              </p>
              <p className="mt-2 font-display text-2xl font-bold uppercase tracking-tight text-ink">
                Book a 1-on-1 consultation with the desk
              </p>
              <p className="mt-2 text-sm text-mute">
                Educational consultation services — market research, sector
                insights, and risk awareness frameworks tailored to your
                learning goals.
              </p>
              <Link
                data-testid="article-booking-cta"
                to="/"
                onClick={() => setTimeout(() => scrollToId("booking"), 150)}
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-white transition-colors hover:bg-brand-crimson"
              >
                Book a Consultation <ArrowRight size={14} />
              </Link>
            </div>

            <p className="mt-8 font-mono text-[10px] leading-relaxed tracking-[0.06em] text-dim">
              This article is educational and informational content only. It
              does not constitute financial, investment, legal, or tax advice.
              Investing involves risk, including the possible loss of principal.
            </p>
          </article>
        )}
      </main>
    </div>
  );
}
